import { produce } from 'immer';

import * as Types from './types';
import { actionTypes } from './constants';

export const defaultState: Types.ICarouselState = {
    activeSlide: 0,
    config: {
        gap: 5, // px
        itemsPerView: 3,
        loop: false,
        navigation: true,
        numeration: false,
        tailWidth: 5 // %
    },
    containerShift: 0,
    direction: 'right',
    itemShift: 0,
    itemWidth: 0,
    numerationData: {
        fontSize: 0,
        width: 0
    },
    prevSlide: 0,
    restart: false,
    slidesCount: 0
};

export const reducer = (reducerSuffix: string) =>
    produce((draftState, action: Types.TCarouselAction): Types.ICarouselState | void => {
        switch (action.type) {
            case actionTypes.MOVE_LEFT:
            case (actionTypes.MOVE_LEFT + reducerSuffix) as actionTypes.MOVE_LEFT: {
                let nextSlide;
                const diff = action.payload;
                const { activeSlide, slidesCount, config: { loop, itemsPerView } } = draftState;

                if (!loop && activeSlide === 0) {
                    return;
                }

                nextSlide = activeSlide - diff;

                if (!loop && activeSlide < itemsPerView && diff > 1) {
                    nextSlide = 0;
                }

                nextSlide = nextSlide >= 0
                    ? nextSlide % slidesCount
                    : slidesCount + nextSlide % slidesCount;

                Object.assign(draftState, {
                    direction: 'left',
                    prevSlide: activeSlide,
                    activeSlide: nextSlide
                });

                break;
            }

            case actionTypes.MOVE_RIGHT:
            case (actionTypes.MOVE_RIGHT + reducerSuffix) as actionTypes.MOVE_RIGHT: {
                let nextSlide;
                const diff = action.payload;
                const { activeSlide, slidesCount, config: { loop, itemsPerView } } = draftState;

                if (!loop && activeSlide >= slidesCount - itemsPerView) {
                    return;
                }

                nextSlide = activeSlide + diff;

                if (!loop && nextSlide > slidesCount - itemsPerView - 1) {
                    nextSlide = slidesCount - itemsPerView;
                }

                nextSlide = nextSlide >= 0
                    ? nextSlide % slidesCount
                    : slidesCount + nextSlide % slidesCount;

                Object.assign(draftState, {
                    direction: 'right',
                    prevSlide: activeSlide,
                    activeSlide: nextSlide
                });

                break;
            }

            case actionTypes.SLIDES_COUNT_UPDATE:
            case (actionTypes.SLIDES_COUNT_UPDATE + reducerSuffix) as actionTypes.SLIDES_COUNT_UPDATE: {
                Object.assign(draftState, {
                    slidesCount: action.payload
                });

                break;
            }

            case actionTypes.CONFIG_UPDATE:
            case (actionTypes.CONFIG_UPDATE + reducerSuffix) as actionTypes.CONFIG_UPDATE: {
                Object.assign(draftState, {
                    config: action.payload
                });

                break;
            }

            case actionTypes.ITEM_WIDTH_UPDATE:
            case (actionTypes.ITEM_WIDTH_UPDATE + reducerSuffix) as actionTypes.ITEM_WIDTH_UPDATE: {
                Object.assign(draftState, {
                    itemWidth: action.payload
                });

                break;
            }

            case actionTypes.CONTAINER_SHIFT_UPDATE:
            case (actionTypes.CONTAINER_SHIFT_UPDATE + reducerSuffix) as actionTypes.CONTAINER_SHIFT_UPDATE: {
                Object.assign(draftState, {
                    containerShift: action.payload
                });

                break;
            }

            case actionTypes.ITEM_SHIFT_UPDATE:
            case (actionTypes.ITEM_SHIFT_UPDATE + reducerSuffix) as actionTypes.ITEM_SHIFT_UPDATE: {
                Object.assign(draftState, {
                    itemShift: action.payload
                });

                break;
            }

            case actionTypes.NUMERATION_DATA_UPDATE:
            case (actionTypes.NUMERATION_DATA_UPDATE + reducerSuffix) as actionTypes.NUMERATION_DATA_UPDATE: {
                Object.assign(draftState, {
                    numerationData: action.payload
                });

                break;
            }

            case actionTypes.TOGGLE_RESTART:
            case (actionTypes.TOGGLE_RESTART + reducerSuffix) as actionTypes.TOGGLE_RESTART: {
                const restart = action.payload;

                Object.assign(draftState, {
                    restart,
                    ...restart && {
                        activeSlide: 0,
                        prevSlide: 0
                    }
                });

                break;
            }

            default: {
                break;
            }
        }
    }, defaultState);
